<script setup>
import { ref, computed } from "vue";
import { useGetters, useActions } from "vuex-composition-helpers";
import ChatbotTranscriptModal from "@/components/ChatbotTranscriptModal";
import { HUMANLY_MAIN_APP_URL } from "@/common/Env";

const emit = defineEmits(["closed"]);
const props = defineProps({
  conversation: {
    type: Object,
    required: true,
    default: () => { },
  },
});

const { conversationTranscript } = useGetters(["conversationTranscript"]);
const { fetchConversationTranscript } = useActions(["fetchConversationTranscript"]);

const show_modal = ref(false);

const messages = computed(() => conversationTranscript.value?.messages || []);
const playbook_name = computed(() => conversationTranscript.value?.playbook_name || "Humanly");
const export_url = computed(() => {
  const path = conversationTranscript.value?.export_link;
  return path ? `${HUMANLY_MAIN_APP_URL}${path} ` : null;
}

);

function formatDate(date_string) {
  if (!date_string) return null;
  const options = { year: "numeric", month: "short", day: "numeric" };
  return new Date(date_string).toLocaleDateString(undefined, options);
}

function formatStatus(status) {
  if (!status) return "Unknown";
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase().replace(/_/g, " ");
}

async function viewTranscript() {
  await fetchConversationTranscript({ id: props.conversation.id });
  show_modal.value = true;
}

function closeModal() {
  show_modal.value = false;
}

function onClose() {
  emit("closed");
}
</script>

<template>
  <div id="conversation">
    <div class="d-flex align-items-center justify-content-between">
      <div>
        <div><b>{{ conversation.title }}</b></div>
        <div v-if="conversation.statusAtAgo" class="text-color-black-50">{{ conversation.statusAtAgo }}</div>
      </div>

      <span class="material-icons-round text-color-black-50 md-18 close-touchpoint" @click="onClose">
        close
      </span>
    </div>

    <div class="card-container mt-16">
      <div class="d-flex justify-content-between mb-8">
        <span class="field">Status</span>
        <span class="field-description" :class="'status-' + conversation.status">{{ formatStatus(conversation.status) }}</span>
      </div>
      <div v-if="conversation.qualified_at" class="d-flex justify-content-between mb-8">
        <span class="field">Qualified</span>
        <span class="field-description">{{ formatDate(conversation.qualified_at) }}</span>
      </div>
      <div v-if="conversation.inserted_at" class="d-flex justify-content-between">
        <span class="field">Started</span>
        <span class="field-description">{{ formatDate(conversation.inserted_at) }}</span>
      </div>
    </div>

    <div class="mt-16">
      <el-button type="primary" size="small" @click="viewTranscript">
        View Transcript
      </el-button>
    </div>

    <ChatbotTranscriptModal
      v-if="show_modal"
      :messages="messages"
      :chatbot-name="playbook_name"
      :completed-at="conversation.statusAt"
      :export-url="export_url"
      @close="closeModal"
    />
  </div>
</template>

<style scoped lang="scss">
.card-container {
  background-color: $black-5;
  padding: 12px;
  border-radius: $tf-global-border-radius;
}

.field {
  color: $black-50;
}

.field-description {
  font-weight: 500;
}

.status-completed {
  color: $green-100;
}

.status-in_progress,
.status-active {
  color: $blue-100;
}

.status-pending {
  color: $tf-sourced-stopped-color;
}

.close-touchpoint {
  cursor: pointer;
}
</style>
